/* Catalog for the Puzzles welcome screen — a believable hedge-fund "macro gym".
   Each game maps to a documented Rokos talent need: the rates/central-bank edge,
   cross-asset macro coherence, conviction calibration, drawdown discipline,
   convexity/carry ("human quant"), and the quant/engineering second track.
   Mock content for the v1 mockup — no game engine is wired yet. */

export type CategoryId =
  | 'rates'
  | 'cross-asset'
  | 'conviction'
  | 'risk'
  | 'convexity'
  | 'quant';

export type Difficulty = 'Intro' | 'Core' | 'Advanced' | 'Elite';
export type GameStatus = 'new' | 'in-progress' | 'solved' | 'locked';

export type Category = { id: CategoryId; label: string; color: string };

/**
 * Where a card's play action comes from:
 *  - 'mock'    — legacy catalog card with no game behind it → shown grayed/inert
 *  - 'embed'   — a native in-app game (Yield Shooter) → always playable
 *  - 'devplan' — a real Development-Plan HTML game → playable once its node unlocks
 * Legacy catalog entries omit `backing` and are treated as 'mock'.
 */
export type GameBacking = 'mock' | 'embed' | 'devplan';

export type Game = {
  id: string;
  title: string;
  blurb: string;
  // Descriptive fields are mock-catalog metadata; real devplan games carry none.
  category?: CategoryId;
  difficulty?: Difficulty;
  minutes?: number;
  skills?: string;
  icon: string; // key resolved to an antd icon in GameCard
  status: GameStatus;
  best?: number; // best score 0–100 (solved / in-progress)
  featured?: boolean;
  embed?: 'yield-shooter' | 'yield-shooter-studio'; // → opens the playable embedded game instead of a mock toast
  hidden?: boolean; // kept in the catalog data but not shown in the grid
  backing?: GameBacking; // undefined ⇒ 'mock' (legacy catalog card)
  assetKey?: string; // devplan game: DevAsset.key (drives the iframe launch)
  filePath?: string; // devplan game: relative asset path
};

export const CATEGORIES: Category[] = [
  { id: 'rates', label: 'Rates & Curves', color: '#0f5fd6' },
  { id: 'cross-asset', label: 'Cross-Asset', color: '#0e9f6e' },
  { id: 'conviction', label: 'Conviction', color: '#7c5cff' },
  { id: 'risk', label: 'Risk & Drawdown', color: '#d14343' },
  { id: 'convexity', label: 'Convexity & Carry', color: '#d48806' },
  { id: 'quant', label: 'Quant & Eng', color: '#1394a8' },
];

export const CATEGORY_BY_ID: Record<CategoryId, Category> = CATEGORIES.reduce(
  (acc, c) => ({ ...acc, [c.id]: c }),
  {} as Record<CategoryId, Category>
);

export const DIFFICULTY_COLORS: Record<Difficulty, string> = {
  Intro: '#0e9f6e',
  Core: '#0f5fd6',
  Advanced: '#d48806',
  Elite: '#d14343',
};

export const GAMES: Game[] = [
  /* ── Rates & Curves — the crown-jewel edge ──────────────────────────── */
  {
    id: 'higher-for-longer',
    title: 'Higher for Longer',
    category: 'rates',
    blurb:
      'Fade a fragile dovish consensus, position before the print, and ride the repricing through the catalyst.',
    difficulty: 'Advanced',
    minutes: 30,
    skills: 'Rates regime · Conviction',
    icon: 'lineChart',
    status: 'new',
    featured: true,
  },
  {
    id: 'yield-shooter',
    title: 'Yield Shooter',
    category: 'rates',
    blurb:
      'Arcade drill on the curve — fire bonds into the right tenor and yield slots before the clock runs out.',
    difficulty: 'Intro',
    minutes: 5,
    skills: 'Curve · Reflexes',
    icon: 'aim',
    status: 'new',
    embed: 'yield-shooter',
    hidden: true, // superseded by the studio-skinned copy below; kept intact, just not shown
  },
  {
    id: 'yield-shooter-studio',
    title: 'Yield Shooter',
    category: 'rates',
    blurb:
      'Arcade drill on the curve — fire bonds into the right tenor and yield slots before the clock runs out.',
    difficulty: 'Intro',
    minutes: 5,
    skills: 'Curve · Reflexes',
    icon: 'aim',
    status: 'new',
    embed: 'yield-shooter-studio',
  },
  {
    id: 'duration-desk',
    title: 'The Duration Desk',
    category: 'rates',
    blurb:
      'Rates +50bp incoming. Find the cheapest hedge that neutralises your book’s DV01.',
    difficulty: 'Core',
    minutes: 10,
    skills: 'Duration · Hedging',
    icon: 'sliders',
    status: 'solved',
    best: 88,
  },
  {
    id: 'curveball',
    title: 'Curveball',
    category: 'rates',
    blurb:
      'Steepener or flattener? Read the regime and express the curve with the right relative-value leg.',
    difficulty: 'Core',
    minutes: 15,
    skills: 'Curve · Relative value',
    icon: 'stock',
    status: 'in-progress',
    best: 41,
  },
  {
    id: 'reaction-function',
    title: 'Reaction Function',
    category: 'rates',
    blurb:
      'Infer the central bank’s next move from the data flow alone — before the market reprices it.',
    difficulty: 'Advanced',
    minutes: 20,
    skills: 'Central banks · Forecasting',
    icon: 'bank',
    status: 'new',
  },

  /* ── Cross-Asset Macro ──────────────────────────────────────────────── */
  {
    id: 'one-thesis',
    title: 'One Thesis, Five Markets',
    category: 'cross-asset',
    blurb:
      'Express a single macro view coherently across rates, FX, equities, commodities and credit.',
    difficulty: 'Advanced',
    minutes: 25,
    skills: 'Cross-asset · Coherence',
    icon: 'deploymentUnit',
    status: 'new',
  },
  {
    id: 'risk-off',
    title: 'Risk-Off',
    category: 'cross-asset',
    blurb:
      'The bid for safety hits. Read the flight-to-quality flows and get to the haven first.',
    difficulty: 'Intro',
    minutes: 5,
    skills: 'Safe havens · Flows',
    icon: 'safety',
    status: 'solved',
    best: 95,
  },
  {
    id: 'contagion',
    title: 'Contagion',
    category: 'cross-asset',
    blurb:
      'One market cracks. Trace the cross-asset transmission and trade it before it spreads.',
    difficulty: 'Advanced',
    minutes: 20,
    skills: 'Transmission · Correlation',
    icon: 'partition',
    status: 'new',
  },

  /* ── Conviction & Calibration ───────────────────────────────────────── */
  {
    id: 'conviction-ladder',
    title: 'Conviction Ladder',
    category: 'conviction',
    blurb:
      'Size to your edge. Calibrate every position to your true confidence — no hedging your bets.',
    difficulty: 'Core',
    minutes: 15,
    skills: 'Sizing · Calibration',
    icon: 'aim',
    status: 'in-progress',
    best: 63,
  },
  {
    id: 'against-the-tape',
    title: 'Against the Tape',
    category: 'conviction',
    blurb:
      'Take the other side of consensus — and be right for the right reason, not just contrarian.',
    difficulty: 'Advanced',
    minutes: 20,
    skills: 'Consensus · Differentiation',
    icon: 'thunderbolt',
    status: 'new',
  },
  {
    id: 'change-your-mind',
    title: 'Change Your Mind',
    category: 'conviction',
    blurb:
      'The evidence just turned against your call. Defend with reasoning, or adapt without ego?',
    difficulty: 'Core',
    minutes: 10,
    skills: 'Adaptive conviction',
    icon: 'sync',
    status: 'new',
  },
  {
    id: 'hundred-million',
    title: '$100 Million',
    category: 'conviction',
    blurb:
      'A blank book and nine figures of capital. Construct the portfolio you’d defend to the risk-taker.',
    difficulty: 'Advanced',
    minutes: 30,
    skills: 'Portfolio construction',
    icon: 'dollar',
    status: 'new',
  },

  /* ── Risk & Drawdown ────────────────────────────────────────────────── */
  {
    id: 'hold-or-fold',
    title: 'Hold or Fold',
    category: 'risk',
    blurb:
      'Down 20% on the thesis and the desk is watching. Cut, add, or hold the line through the pain?',
    difficulty: 'Elite',
    minutes: 20,
    skills: 'Drawdown discipline',
    icon: 'fall',
    status: 'locked',
  },
  {
    id: 'margin-call',
    title: 'Margin Call',
    category: 'risk',
    blurb:
      'Survive the squeeze. Manage collateral and leverage through a liquidity crunch without forced selling.',
    difficulty: 'Advanced',
    minutes: 15,
    skills: 'Leverage · Collateral',
    icon: 'warning',
    status: 'new',
  },
  {
    id: 'risk-budget',
    title: 'Risk Budget',
    category: 'risk',
    blurb:
      'Spend a fixed risk envelope across competing bets so no single trade can sink the book.',
    difficulty: 'Core',
    minutes: 15,
    skills: 'Risk budgeting',
    icon: 'pieChart',
    status: 'solved',
    best: 79,
  },

  /* ── Convexity & Carry — the "human quant" ──────────────────────────── */
  {
    id: 'convexity',
    title: 'Convexity',
    category: 'convexity',
    blurb:
      'Build asymmetric upside with capped, known downside. Pay for the option, not the direction.',
    difficulty: 'Elite',
    minutes: 20,
    skills: 'Asymmetry · Options',
    icon: 'radarChart',
    status: 'locked',
  },
  {
    id: 'carry-and-roll',
    title: 'Carry & Roll',
    category: 'convexity',
    blurb:
      'Harvest the carry without dying on the roll-down. Earn while you wait — and know when to fold.',
    difficulty: 'Core',
    minutes: 15,
    skills: 'Carry · Roll-down',
    icon: 'retweet',
    status: 'new',
  },

  /* ── Quant & Engineering — the second talent track ──────────────────── */
  {
    id: 'optimal-execution',
    title: 'Optimal Execution',
    category: 'quant',
    blurb:
      'Liquidate a large position against the clock without moving the market against yourself.',
    difficulty: 'Advanced',
    minutes: 15,
    skills: 'Execution · Slippage',
    icon: 'rocket',
    status: 'new',
  },
  {
    id: 'efficient-frontier',
    title: 'The Efficient Frontier',
    category: 'quant',
    blurb:
      'Optimise the risk-return trade-off under real-world constraints. Where does tangency sit?',
    difficulty: 'Core',
    minutes: 15,
    skills: 'Optimisation',
    icon: 'dotChart',
    status: 'solved',
    best: 84,
  },
  {
    id: 'signal-vs-noise',
    title: 'Signal vs Noise',
    category: 'quant',
    blurb:
      'Separate real alpha from luck in a return series before you size a single dollar behind it.',
    difficulty: 'Advanced',
    minutes: 15,
    skills: 'Statistics · Alpha',
    icon: 'function',
    status: 'new',
  },
  {
    id: 'backtest-trap',
    title: 'Backtest Trap',
    category: 'quant',
    blurb:
      'Find the lookahead bias hiding in a “perfect” strategy before it costs the desk real money.',
    difficulty: 'Advanced',
    minutes: 10,
    skills: 'Overfitting · Rigour',
    icon: 'experiment',
    status: 'new',
  },
];

export const PROFILE = {
  rating: 1840,
  solved: GAMES.filter(g => g.status === 'solved' && !g.hidden).length,
  total: GAMES.filter(g => !g.hidden).length,
  streak: 5,
  percentile: 'Top 12%',
};
